import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import CustomInputField from "components/common/CustomInputField";
import { successToaster, ErrorToaster } from "components/common/toast";
import handleError from "utils/handleError";
import BtnCustom from "components/common/btn-custom";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomPaginatedSelect from "components/common/CustomPaginatedSelect";
import { InputGroup } from "reactstrap";
import ImageUploadCustom from "components/CustomUpload/ImageUploadCustom";
import { WineDataFieldsOrdered as WineDataFields, WIDE_TEXT_FIELDS } from "constant/Fields";
import { AttributesImageFields } from "constant/Fields";
import { UpdateDataSet } from "network/wineDataSetAPI";
import { CreateDataSet } from "network/wineDataSetAPI";
import PATHS from "constant/paths";
import { WineDataSetSchema } from "components/schema/WineDataSetSchema";
import { formatDecimalWithComma } from "utils/formatDecimalWithComma";
import { ImageUrlToFile } from "utils/ImageUrlToFile";
import CountryListSelect from "components/common/CounteryListSlecte";
import { mediaUrl } from "utils/mediaUrl";

const DataSetFrom = ({ editData }) => {
  const { t } = useTranslation("wine");
  const navigate = useNavigate();
  const [errorImg, setErrorImg] = useState({});

  const initialValues = {
    name: editData?.name || "",
    description: editData?.description || "",
    front_bottle_image: editData?.front_bottle_image
      ? mediaUrl(editData?.front_bottle_image)
      : null,
    back_bottle_image: editData?.back_bottle_image
      ? mediaUrl(editData?.back_bottle_image)
      : null,
    front_label_image: editData?.front_label_image
      ? mediaUrl(editData?.front_label_image)
      : null,
    back_label_image: editData?.back_label_image
      ? mediaUrl(editData?.back_label_image)
      : null,
    ean: editData?.ean || "",
    winery: editData?.winery || "",
    vintage: formatDecimalWithComma(editData?.vintage) || "",
    denomination: editData?.denomination || "",
    gradation: formatDecimalWithComma(editData?.gradation) || "",
    vinification: editData?.vinification || "",
    terroir: editData?.terroir || "",
    region: editData?.region || "",
    format: formatDecimalWithComma(editData?.format) || "",
    country: editData?.country ?? null,
    natural: editData?.natural ?? null,
    bio: editData?.bio ?? null,
    have_sulphite: editData?.have_sulphite ?? null,
    biodynamic: editData?.biodynamic ?? null,
    conventional: editData?.conventional ?? null,
    energy: editData?.energy || "",
    fat: formatDecimalWithComma(editData?.fat) || "",
    saturated_fat: formatDecimalWithComma(editData?.saturated_fat) || "",
    carbohydrate: formatDecimalWithComma(editData?.carbohydrate) || "",
    sugar: formatDecimalWithComma(editData?.sugar) || "",
    protein: formatDecimalWithComma(editData?.protein) || "",
    salt: formatDecimalWithComma(editData?.salt) || "",
    ingredients: editData?.ingredients || "",
    fermentation: editData?.fermentation || "",
    colour: editData?.colour || "",
    appearance: editData?.appearance || "",
    aroma: editData?.aroma || "",
    taste: editData?.taste || "",
    pairing: editData?.pairing || "",

    // is_active: editData?.is_active ?? true,
    // is_approved: editData?.is_approved ?? true,
  };
  const convertToFormData = async (values) => {
    const formData = new FormData();

    const imageFields = [
      "front_bottle_image",
      "back_bottle_image",
      "front_label_image",
      "back_label_image",
    ];
    await Promise.all(
      imageFields.map(async (field) => {
        const value = values[field];
        if (value instanceof File) {
          formData.append(field, value);
        } else if (typeof value === "string" && value.startsWith("http")) {
          const file = await ImageUrlToFile(value);
          if (file) {
            formData.append(field, file);
          } else {
            formData.append(field, "");
          }
        } else {
          formData.append(field, "");
        }
      })
    );

    if (values.sub_categories && Array.isArray(values.sub_categories)) {
      values.sub_categories.forEach((item) => {
        formData.append("sub_categories", item?.value);
      });
    }

    Object.keys(values).forEach((key) => {
      if (!imageFields.includes(key) && key !== "sub_categories") {
        const value = values[key];

        if (value instanceof File || value instanceof Blob) {
          formData.append(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            formData.append(`${key}[${index}]`, item);
          });
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      }
    });

    return formData;
  };
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    resetForm,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: WineDataSetSchema,
    onSubmit: async (values) => {
      const formData = await convertToFormData(values);

      try {
        let response;
        if (editData) {
          response = await UpdateDataSet(formData, editData?.uuid);
        } else {
          response = await CreateDataSet(formData);
        }

        if (response.success) {
          successToaster(response.message);
          navigate(PATHS.WINE_DATA_SET);
          resetForm();
        }
      } catch (error) {
        ErrorToaster(handleError(error?.status_code, error?.message));
      }
    },
  });

  const handleReset = () => {
    resetForm({ values: { ...initialValues, country: null } });
  };
  return (
    <>
      <div className="mt-2">
        <form onSubmit={handleSubmit}>
          <div className="row">
            {WineDataFields.map(
              ({ name, type, options, unit, required, staticOptions1 }) => {
                return (
                  <div
                    key={name}
                    className={`col-12 mb-2 ${WIDE_TEXT_FIELDS.includes(name) ? "" : "col-sm-6 col-md-6 col-lg-3"}`}
                  >
                    {(type === "text" || type === "textarea") && (
                      <CustomInputField
                        label={`${t(`fields.${name}`)}${
                          required ? " *" : ""
                        }`}
                        name={name}
                        placeholder={t(`fields.${name}`)}
                        type={type}
                        errors={errors}
                        touched={touched}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values}
                        unit={unit}
                        {...(WIDE_TEXT_FIELDS.includes(name) ? { rows: name === "description" ? 5 : 3 } : {})}
                      />
                    )}
                    {type === "select" && (
                      <CustomPaginatedSelect
                        key={name}
                        label={t(`fields.${name}`).toUpperCase()}
                        name={name}
                        options={options}
                        errors={errors}
                        touched={touched}
                        isSearchable={false}
                        onChange={(selectedOption) => {
                          setFieldValue(name, selectedOption?.value ?? null);
                        }}
                        onBlur={handleBlur}
                        value={
                          staticOptions1.find((opt) => {
                            const optionValue =
                              values[name] === true
                                ? true
                                : values[name] === false
                                ? false
                                : null;
                            return opt.value === optionValue;
                          }) || null
                        }
                        staticOptions={staticOptions1}
                      />
                    )}
                    {type === "selectCountry" && (
                      <CountryListSelect
                        label={t("fields.country")}
                        name="country"
                        errors={errors}
                        touched={touched}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        value={values["country"]}
                      />
                    )}
                  </div>
                );
              }
            )}
          </div>
          <div className="row">
            {AttributesImageFields?.map(({ name }) => {
              return (
                <div
                  key={name}
                  className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 "
                >
                  <div className="custom_input">
                    <InputGroup>
                      <ImageUploadCustom
                        label={t(`fields.${name}`).toUpperCase()}
                        name={name}
                        changeBtnClasses="btn-simple"
                        addBtnClasses="btn-simple"
                        removeBtnClasses="btn-simple"
                        setFieldValue={setFieldValue}
                        value={values[name]}
                        errors={errorImg}
                        setError={setErrorImg}
                      />
                    </InputGroup>
                  </div>
                </div>
              );
            })}
          </div>

          <BtnCustom
            isLoading={isSubmitting}
            disabled={isSubmitting}
            type="submit"
            color="btn btn-primary mt-4 mr-3 block"
            submittingText={editData ? t("form.updating") : t("form.adding")}
          >
            {editData ? t("form.update") : t("form.add")}
          </BtnCustom>
          {editData ? (
            ""
          ) : (
            <BtnCustom
              disabled={isSubmitting}
              type="button"
              color="btn btn-secondary mt-4 block text-white"
              onClick={handleReset}
            >
              {t("form.reset")}
            </BtnCustom>
          )}
        </form>
      </div>
    </>
  );
};

export default DataSetFrom;
